import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiEdit, FiTrash } from "react-icons/fi";
import { GoPrimitiveDot } from "react-icons/go";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import Swal from "sweetalert2";
import { APP_CONTEXT } from "../../../utils/context";
import PageHeader from "../../../components/PageHeader/PageHeader.component";
import Button from "../../../components/Button/Button.component";
import Table, { TableIcons } from "../../../components/Table/Table.component";
import Notification from "../../../components/Toast/Toast.component";

// GQL
import { DELETE_SPORT_TYPE } from "../../../graphql/mutations/sportType.mutation";
import {
  GET_SPORT_TYPES,
  GET_SPORT_TYPES_EXPORT,
} from "../../../graphql/queries/sportType.query";
import { GET_ALL_ACTIVE_SPORT_CATEGORIES } from "../../../graphql/queries/sportCategory.query";
import { formatGraphqlError } from "../../../utils/api";

const SportTypeAll = () => {
  const { setActiveSideNav, setPageTitle } = useContext(APP_CONTEXT);
  const [refetch, { data, loading, error }] = useLazyQuery(GET_SPORT_TYPES, {
    variables: { order: "title_ASC" },
    fetchPolicy: "no-cache",
  });
  const sportCategories = useQuery(GET_ALL_ACTIVE_SPORT_CATEGORIES, {
    fetchPolicy: "no-cache",
  });
  const [variables, setVariables] = useState({
    where: { AND: [{ NOT: { status: "ARCHIVED" } }] },
  });
  const [deleteSportType] = useMutation(DELETE_SPORT_TYPE);
  const [getSportTypesExport, sportTypesExport] = useLazyQuery(
    GET_SPORT_TYPES_EXPORT
  );

  useEffect(() => {
    setActiveSideNav("sysConf");
    setPageTitle("Manage SportTypes");
  });

  const handleDelete = (e, row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.value) {
        deleteSportType({
          variables: {
            id: row.original.id,
          },
        })
          .then(() => {
            Swal.fire("Deleted!", "Sport Type has been deleted", "success");
            refetch({
              variables: {
                ...variables,
                first: 50,
                skip: 0,
              },
            });
          })
          .catch((err) =>
            Notification.error({
              title: "Delete Sport Type Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  const getColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "#39C568";
      case "ARCHIVE":
        return "#FF003A";
      case "SUGGESTION":
        return "#ef6e2d";
      case "DRAFT":
        return "#707172";
      default:
        return "#707172";
    }
  };

  const HeaderInfo = [
    {
      Header: "Name",
      accessor: "title",
      id: "title",
      search: true,
    },
    {
      Header: "Description",
      accessor: "description",
      id: "description",
      search: true,
    },
    {
      Header: "Category",
      id: "sportCategory",
      Cell: ({ row }) =>
        row.original.sportCategory && row.original.sportCategory.title,
    },
    {
      Header: "Status",
      id: "status",
      Cell: ({ row }) => (
        <TableIcons>
          <GoPrimitiveDot
            style={{ fontSize: "25px", color: getColor(row.original.status) }}
          />
          <span>{row.original.status.toLowerCase()}</span>
        </TableIcons>
      ),
    },
    {
      Header: "Action",
      id: "actions",
      Cell: ({ row }) => (
        <TableIcons>
          <Link to={`/app/system-configuration/sport-type/${row.original.id}`}>
            <FiEdit
              style={{ fontSize: "20px", color: "#07002F", cursor: "pointer" }}
            />
          </Link>
          <FiTrash
            onClick={(e) => handleDelete(e, row)}
            style={{ fontSize: "20px", color: "#07002F", cursor: "pointer" }}
          />
        </TableIcons>
      ),
    },
  ];

  const getSportCategories = (sportCategoriesArr) => {
    return sportCategoriesArr.map((sportCategory) => ({
      value: sportCategory.id,
      label: sportCategory.title,
    }));
  };

  const filters = [
    {
      header: "Date Created:",
      accessor: "createdAt",
      type: "daterange",
    },
    {
      header: "Sport Categories:",
      accessor: "sportCategory",
      placeholder: "Please select sport types",
      type: "multiselect",
      options: sportCategories.data
        ? getSportCategories(sportCategories.data.sportCategories)
        : [],
      customFilter: (sportCategory) => {
        let retVal = null;
        if (sportCategory.value.length) {
          retVal = {};
          retVal.sportCategory = {
            id_in: sportCategory.value.map((val) => val.value),
          };
        }
        return retVal;
      },
    },
    {
      header: "Status:",
      accessor: "status",
      placeholder: "Please select a Status",
      type: "select",
      options: [
        { value: "ACTIVE", text: "Active" },
        { value: "ARCHIVE", text: "Archive" },
        { value: "DRAFT", text: "Draft" },
        { value: "SUGGESTION", text: "Suggestion" },
      ],
    },
  ];

  return (
    <>
      <PageHeader title="Manage Sport Types">
        <Link to="/app/system-configuration/sport-type/new">
          <Button
            onClick={() => {}}
            kind="secondary"
            value="New Sport Type"
            width="177px"
            margin="10px 0 10px 10px"
          />
        </Link>
      </PageHeader>
      <Table
        columns={HeaderInfo}
        data={(data && data.sportTypes) || []}
        loading={loading}
        error={error}
        refetch={refetch}
        variables={variables}
        dataCount={(data && data.sportTypesCount.length) || 0}
        pageCount={5}
        setVariables={setVariables}
        tableTitle="Sport Types"
        tableOptions={["search", "download", "columns", "filter"]}
        filters={filters}
        downloadData={sportTypesExport}
        getDownloadData={getSportTypesExport}
        downloadAccessor={
          sportTypesExport.data ? sportTypesExport.data.sportTypes : []
        }
        fileName="Sport Types"
      />
    </>
  );
};

export default SportTypeAll;
